import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { AlertTriangle, CheckCircle2, Info, Loader2, Minus, Plus, X, XCircle } from 'lucide-react';

export const cx = (...c: unknown[]) =>
  c.filter((x): x is string => typeof x === 'string' && x.length > 0).join(' ');

// ---- Button ------------------------------------------------------------------------------------------------
// Pill-shaped, deliberate proportions, 110 ms press scale. primary = midnight ink, accent = signal amber (ink text,
// soft glow) for the single most important action on a screen, brand = navy, secondary = white with hairline.
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent' | 'outline' | 'brand' | 'glass';
const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-ink-900 text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.08),0_6px_16px_-8px_rgb(7_11_22/0.6)] hover:bg-ink-800 active:bg-ink-950 disabled:bg-ink-200 disabled:text-ink-500 disabled:shadow-none',
  brand:
    'bg-peacock-600 text-white shadow-[0_6px_16px_-8px_rgb(16_26_68/0.7)] hover:bg-peacock-700 active:bg-peacock-800 disabled:bg-ink-200 disabled:text-ink-500 disabled:shadow-none',
  accent:
    'bg-marigold-500 text-ink-950 shadow-[var(--shadow-glow)] hover:bg-[#ffb63f] active:bg-marigold-600 disabled:bg-ink-200 disabled:text-ink-500 disabled:shadow-none',
  secondary:
    'bg-white text-ink-900 shadow-[var(--shadow-card)] hover:bg-ink-50 active:bg-ink-100 disabled:text-ink-400',
  outline:
    'bg-transparent text-ink-900 ring-[1.5px] ring-inset ring-ink-900/85 hover:bg-ink-900/[0.04] active:bg-ink-900/[0.08] disabled:text-ink-400 disabled:ring-ink-200',
  ghost: 'text-ink-700 hover:bg-ink-900/[0.05] active:bg-ink-900/[0.08] disabled:text-ink-400',
  danger: 'bg-danger-500 text-white hover:bg-danger-600 disabled:bg-ink-200 disabled:text-ink-500',
  glass: 'rb-glass text-white hover:bg-white/15 active:bg-white/20',
};
const SIZES = {
  sm: 'h-9 px-4 text-[13px] rounded-full',
  md: 'h-11 px-5 text-[15px] rounded-full',
  lg: 'h-[54px] px-6 text-[16px] rounded-full',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  block = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: keyof typeof SIZES;
  loading?: boolean;
  block?: boolean;
}) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'rb-press inline-flex items-center justify-center gap-2 font-semibold tracking-[-0.01em] select-none disabled:cursor-not-allowed disabled:active:scale-100',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        className,
      )}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
}

export function IconButton({
  label,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      {...rest}
      aria-label={label}
      title={label}
      className={cx(
        'rb-press inline-grid h-11 w-11 place-items-center rounded-full text-ink-700 hover:bg-ink-900/[0.05] active:bg-ink-900/[0.08]',
        className,
      )}
    >
      {children}
    </button>
  );
}

// ---- Surfaces ------------------------------------------------------------------------------------------------
export function Card({
  className,
  children,
  as: As = 'div',
  ...rest
}: { className?: string; children: ReactNode; as?: 'div' | 'section' | 'article' | 'li' } & Record<
  string,
  unknown
>) {
  return (
    <As {...rest} className={cx('rounded-[20px] bg-surface shadow-[var(--shadow-card)]', className)}>
      {children}
    </As>
  );
}

type Tone = 'neutral' | 'brand' | 'accent' | 'success' | 'danger' | 'warning' | 'info' | 'dark';
const TONES: Record<Tone, string> = {
  neutral: 'bg-ink-100 text-ink-700',
  brand: 'bg-peacock-50 text-peacock-700',
  accent: 'bg-marigold-100 text-marigold-700',
  success: 'bg-success-50 text-success-600',
  danger: 'bg-danger-50 text-danger-600',
  warning: 'bg-warning-50 text-warning-600',
  info: 'bg-info-50 text-info-600',
  dark: 'bg-ink-900 text-white',
};
export function Badge({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: Tone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cx(
        'inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-[11.5px] font-semibold whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Divider({ className }: { className?: string }) {
  return <hr className={cx('border-0 border-t border-ink-100', className)} />;
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cx('rb-skeleton', className)} />;
}

export function Spinner({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <span role="status" className={cx('inline-flex items-center gap-2 text-ink-500', className)}>
      <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('flex flex-col items-center px-6 py-12 text-center', className)}>
      {icon && (
        <div className="mb-4 grid h-[72px] w-[72px] place-items-center rounded-[24px] bg-white text-ink-800 shadow-[var(--shadow-raised)]">
          {icon}
        </div>
      )}
      <h3 className="rb-display text-[21px] font-bold text-ink-900">{title}</h3>
      {body && <p className="mt-1 max-w-sm text-sm text-ink-500">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success';
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const Icon =
    tone === 'danger'
      ? XCircle
      : tone === 'warning'
        ? AlertTriangle
        : tone === 'success'
          ? CheckCircle2
          : Info;
  const tones = {
    info: 'bg-info-50 text-info-600 border-info-600/15',
    warning: 'bg-warning-50 text-warning-600 border-warning-600/20',
    danger: 'bg-danger-50 text-danger-600 border-danger-600/15',
    success: 'bg-success-50 text-success-600 border-success-600/15',
  };
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cx('flex gap-3 rounded-[16px] border p-3.5 text-sm leading-relaxed', tones[tone], className)}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      <div className="min-w-0">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={cx(title ? 'mt-0.5' : null, 'text-ink-700')}>{children}</div>}
      </div>
    </div>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'up' | 'down' | 'flat';
}) {
  return (
    <div className="min-w-0">
      <div className="rb-eyebrow text-ink-500">{label}</div>
      <div className="rb-display rb-tabular mt-1.5 text-[26px] font-bold leading-none text-ink-900">
        {value}
      </div>
      {hint && (
        <div
          className={cx(
            'mt-0.5 text-xs',
            tone === 'down' ? 'text-danger-600' : tone === 'up' ? 'text-success-600' : 'text-ink-500',
          )}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

// ---- Forms --------------------------------------------------------------------------------------------------
export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
  required,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  htmlFor?: string;
  required?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-[13px] font-semibold text-ink-700">
        {label}
        {required && (
          <span className="ml-0.5 text-danger-500" aria-hidden>
            *
          </span>
        )}
      </label>
      {children}
      {error ? (
        <p role="alert" className="text-xs font-medium text-danger-600">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-ink-500">{hint}</p>
      ) : null}
    </div>
  );
}

const inputBase =
  'w-full rounded-[14px] border border-ink-200/90 bg-white px-4 text-[15px] text-ink-900 placeholder:text-ink-400 transition-[border-color,box-shadow] duration-200 hover:border-ink-300 focus:border-ink-900 focus:outline-none focus:ring-4 focus:ring-marigold-500/25 disabled:bg-ink-50 disabled:text-ink-500 aria-[invalid=true]:border-danger-500';

export function Input({
  className,
  invalid,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return <input {...rest} aria-invalid={invalid || undefined} className={cx(inputBase, 'h-12', className)} />;
}

export function Textarea({
  className,
  invalid,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      {...rest}
      aria-invalid={invalid || undefined}
      className={cx(inputBase, 'min-h-24 py-3', className)}
    />
  );
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...rest}
      className={cx(
        inputBase,
        'h-12 appearance-none bg-[length:16px] bg-[right_12px_center] bg-no-repeat pr-10',
        className,
      )}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%235b6573' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
      }}
    >
      {children}
    </select>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 disabled:opacity-50',
        checked ? 'bg-ink-900' : 'bg-ink-200',
      )}
    >
      <span
        className={cx(
          'inline-block h-5 w-5 rounded-full shadow transition-transform duration-200 ease-[cubic-bezier(.16,1,.3,1)]',
          checked ? 'translate-x-6 bg-marigold-500' : 'translate-x-1 bg-white',
        )}
      />
    </button>
  );
}

export function Checkbox({
  checked,
  onChange,
  children,
  id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
  id?: string;
}) {
  const auto = useId();
  const cid = id ?? auto;
  return (
    <label htmlFor={cid} className="flex cursor-pointer items-start gap-3 text-sm text-ink-700">
      <input
        id={cid}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 accent-[var(--color-ink-900)]"
      />
      <span>{children}</span>
    </label>
  );
}

export function QuantityStepper({
  value,
  onChange,
  max = 24,
  label,
  busy,
  size = 'md',
}: {
  value: number;
  onChange: (v: number) => void;
  max?: number;
  label: string;
  busy?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const h =
    size === 'lg'
      ? 'h-11 min-w-[120px]'
      : size === 'sm'
        ? 'h-8 min-w-[74px] px-3 text-[12px]'
        : 'h-9 min-w-[88px]';
  if (value === 0) {
    return (
      <button
        type="button"
        onClick={() => onChange(1)}
        aria-label={`Add ${label}`}
        disabled={busy}
        className={cx(
          'rb-press inline-flex items-center justify-center gap-1 rounded-full bg-white px-4 text-[13px] font-bold tracking-wide text-ink-900 shadow-[0_0_0_1.5px_rgb(14_19_36/0.9),0_4px_12px_-6px_rgb(14_19_36/0.35)] hover:bg-marigold-50 disabled:opacity-60',
          h,
        )}
      >
        ADD <Plus className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
      </button>
    );
  }
  return (
    <div
      className={cx(
        'inline-flex animate-[fade-in_160ms_ease-out] items-center justify-between rounded-full bg-ink-900 px-1 text-white shadow-[0_6px_16px_-8px_rgb(7_11_22/0.7)]',
        h,
      )}
      role="group"
      aria-label={`Quantity of ${label}`}
    >
      <button
        type="button"
        className="rb-press grid h-7 w-7 place-items-center rounded-full hover:bg-white/10"
        onClick={() => onChange(value - 1)}
        aria-label={`Remove one ${label}`}
        disabled={busy}
      >
        <Minus className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
      </button>
      <span className="rb-num min-w-5 text-center text-[14px] font-semibold" aria-live="polite">
        {busy ? <Loader2 className="mx-auto h-3.5 w-3.5 animate-spin" aria-hidden /> : value}
      </span>
      <button
        type="button"
        className="rb-press grid h-7 w-7 place-items-center rounded-full text-marigold-500 hover:bg-white/10 disabled:opacity-40"
        onClick={() => onChange(value + 1)}
        aria-label={`Add one more ${label}`}
        disabled={busy || value >= max}
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
      </button>
    </div>
  );
}

// ---- Overlays ------------------------------------------------------------------------------------------------
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    ref.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="presentation">
      <div
        className="absolute inset-0 animate-[fade-in_200ms_ease-out] bg-ink-950/55 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={cx(
          !footer && 'rb-safe-bottom',
          'relative max-h-[calc(100dvh-max(env(safe-area-inset-top),var(--safe-area-inset-top,0px))-16px)] w-full max-w-lg animate-[slide-up_320ms_cubic-bezier(0.16,1,0.3,1)] overflow-y-auto rounded-t-[28px] bg-white shadow-[var(--shadow-sheet)] outline-none sm:rounded-[28px]',
        )}
      >
        <div className="sticky top-0 z-10 bg-white/95 px-5 pb-3 pt-2.5 backdrop-blur">
          <div className="mx-auto mb-2.5 h-1 w-10 rounded-full bg-ink-200" aria-hidden />
          <div className="flex items-center justify-between gap-3">
            <div className="rb-display text-[21px] font-bold text-ink-900">{title}</div>
            <IconButton label="Close" onClick={onClose} className="-mr-1 h-9 w-9 bg-ink-100/80">
              <X className="h-[18px] w-[18px]" />
            </IconButton>
          </div>
        </div>
        <div className="px-5 pb-5 pt-1">{children}</div>
        {/* With a footer, the safe-area inset is padding on the footer itself, so nothing scrolls visibly beneath it. */}
        {footer && (
          <div className="sticky bottom-0 border-t border-ink-100 bg-white px-5 pt-3 pb-[max(12px,calc(env(safe-area-inset-bottom)+8px),calc(var(--safe-area-inset-bottom,0px)+8px))]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function Tabs<T extends string>({
  value,
  onChange,
  tabs,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  tabs: { value: T; label: ReactNode; count?: number }[];
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={cx('rb-scroll-x flex gap-1 overflow-x-auto rounded-full bg-ink-900/[0.05] p-1', className)}
    >
      {tabs.map((t) => (
        <button
          key={t.value}
          role="tab"
          aria-selected={value === t.value}
          onClick={() => onChange(t.value)}
          className={cx(
            'flex h-9 shrink-0 items-center gap-1.5 rounded-full px-4 text-[13px] font-semibold transition-all duration-200',
            value === t.value
              ? 'bg-white text-ink-900 shadow-[var(--shadow-card)]'
              : 'text-ink-500 hover:text-ink-900',
          )}
        >
          {t.label}
          {t.count !== undefined && (
            <span
              className={cx(
                'rb-num rounded-full px-1.5 text-[11px]',
                value === t.value ? 'bg-marigold-500 text-ink-950' : 'bg-ink-200 text-ink-700',
              )}
            >
              {t.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ---- Toasts (used sparingly) -------------------------------------------------------------------------------
interface ToastItem {
  id: number;
  tone: 'success' | 'danger' | 'info';
  message: string;
}
const ToastCtx = createContext<(tone: ToastItem['tone'], message: string) => void>(() => undefined);
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((tone: ToastItem['tone'], message: string) => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x.slice(-2), { id, tone, message }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), 4200);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 top-3 z-[60] flex flex-col items-center gap-2 px-4"
      >
        {items.map((t) => (
          <div
            key={t.id}
            className={cx(
              'pointer-events-auto mt-[env(safe-area-inset-top)] flex max-w-md animate-[slide-up_320ms_cubic-bezier(0.16,1,0.3,1)] items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold shadow-[var(--shadow-float)]',
              t.tone === 'success'
                ? 'bg-ink-900 text-white'
                : t.tone === 'danger'
                  ? 'bg-danger-600 text-white'
                  : 'bg-white text-ink-900',
            )}
          >
            {t.tone === 'success' ? (
              <CheckCircle2 className="h-4 w-4 text-marigold-500" />
            ) : t.tone === 'danger' ? (
              <XCircle className="h-4 w-4" />
            ) : (
              <Info className="h-4 w-4 text-peacock-600" />
            )}
            {t.message}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// ---- Layout helpers ----------------------------------------------------------------------------------------
export function PageHeader({
  title,
  subtitle,
  actions,
  back,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  back?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-2">
        {back}
        <div className="min-w-0">
          <h1 className="rb-display truncate text-[26px] font-bold leading-tight text-ink-900 sm:text-[28px]">
            {title}
          </h1>
          {subtitle && <p className="mt-1 text-sm text-ink-500">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function DemoRibbon({ label = 'Demo · simulated stores, orders and payments' }: { label?: string }) {
  return (
    <div role="note" className="flex items-center justify-center gap-2 bg-night-900 px-4 py-1.5 text-center">
      <span className="h-1.5 w-1.5 rounded-full bg-marigold-500" aria-hidden />
      <span className="rb-eyebrow text-[10px] text-marigold-200">{label}</span>
    </div>
  );
}
